/**
 * PBI 1962 (AB#1962 / AB#1968) — Owner manual-booking option selection.
 *
 * Pure helpers behind the AddEditAppointment option picker. No React, no I/O.
 *
 * Semantics (Faris tech-brief §1.3 / §4.3 — HYBRID pricing):
 *   - Variant group (Single) → the chosen option's price/duration are ABSOLUTE.
 *   - Add-on group (Multiple) → each chosen option's price/duration are
 *     ADDITIVE deltas on top of the variant.
 *   - total.price    = variant.price    + Σ addOn.price
 *   - total.duration = variant.duration + Σ addOn.duration
 *
 * The server is AUTHORITATIVE: it re-validates the selection and recomputes
 * the total (overriding the client amount). Everything here is advisory,
 * fast-feedback UI state.
 */
import type { ServiceOptionGroupDto } from '../../types/serviceOptionGroups';
import { durationStringToMinutes } from './serviceOptionGroups';

export interface AppointmentOptionSelection {
    variantOptionId?: number;
    addOnOptionIds: number[];
}

export const emptySelection = (): AppointmentOptionSelection => ({
    variantOptionId: undefined,
    addOnOptionIds: [],
});

/** One itemized line from the owner appointment-detail read (AppointmentSelectedOptionDto). */
export interface AppointmentSelectedOptionLine {
    serviceOptionId: number;
    serviceOptionGroupId: number;
    /** Serialized as the enum NAME on the wire (AB#1894 convention). */
    kind: 'Variant' | 'AddOn';
    name: string;
    price: number;
    /** "HH:mm:ss" TimeSpan string. */
    duration: string;
    sortOrder: number;
}

/**
 * Feature detection for the picker. The service DTO carries `optionGroups`
 * only when the backend `service_option_groups` flag is ON (empty/absent
 * otherwise), so presence — not the FE env flag alone — decides whether the
 * grouped picker renders. Requires a Variant group with at least one option
 * (the backend guarantees one via the M54 auto-wrap backfill when ON).
 */
export const hasBookableOptionGroups = (
    groups: ServiceOptionGroupDto[] | null | undefined,
): groups is ServiceOptionGroupDto[] =>
    Array.isArray(groups) &&
    groups.some((g) => g.kind === 'Variant' && g.options.length > 0);

/** The (single, v1) Variant group. */
export const findVariantGroup = (
    groups: ServiceOptionGroupDto[],
): ServiceOptionGroupDto | undefined =>
    [...groups]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .find((g) => g.kind === 'Variant' && g.options.length > 0);

/** Add-on groups in sort order (may be empty). */
export const findAddOnGroups = (
    groups: ServiceOptionGroupDto[],
): ServiceOptionGroupDto[] =>
    [...groups]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .filter((g) => g.kind === 'AddOn' && g.options.length > 0);

/**
 * Default selection for a freshly chosen service: pre-select the sole variant
 * option when the group has exactly one (mirrors the mobile Phase-4 sheet);
 * multi-option variants start unselected (the owner must choose — the backend
 * rejects with APPOINTMENT.VARIANT_REQUIRED otherwise). No add-ons.
 */
export const defaultSelection = (
    groups: ServiceOptionGroupDto[],
): AppointmentOptionSelection => {
    const variant = findVariantGroup(groups);
    return {
        variantOptionId:
            variant && variant.options.length === 1 ? variant.options[0].id : undefined,
        addOnOptionIds: [],
    };
};

/**
 * Live totals for the chosen selection. Returns null until a variant is
 * chosen (there is no meaningful base before that). Duration in whole minutes.
 */
export const computeSelectionTotals = (
    groups: ServiceOptionGroupDto[],
    selection: AppointmentOptionSelection,
): { price: number; durationMin: number; currency: string } | null => {
    const variant = findVariantGroup(groups);
    const chosenVariant = variant?.options.find(
        (o) => o.id === selection.variantOptionId,
    );
    if (!chosenVariant) return null;

    let price = chosenVariant.price;
    let durationMin = durationStringToMinutes(chosenVariant.duration);

    const addOnById = new Map(
        findAddOnGroups(groups).flatMap((g) => g.options.map((o) => [o.id, o] as const)),
    );
    for (const id of selection.addOnOptionIds) {
        const addOn = addOnById.get(id);
        if (!addOn) continue; // stale id (service switched) — ignored, also filtered at submit
        price += addOn.price;
        durationMin += durationStringToMinutes(addOn.duration);
    }
    return { price, durationMin, currency: chosenVariant.currency };
};

export interface SelectionValidationIssue {
    /** i18n key. */
    key: string;
    /** Interpolation for {n}; group name for {group}. */
    n?: number;
    group?: string;
}

/**
 * Advisory client-side validation mirroring the backend rules:
 *   - a variant choice is required;
 *   - each add-on group's selected count must satisfy minSelect..maxSelect.
 * The backend re-validates authoritatively (VARIANT_REQUIRED /
 * OPTION_SELECTION_INVALID).
 */
export const validateSelection = (
    groups: ServiceOptionGroupDto[],
    selection: AppointmentOptionSelection,
): SelectionValidationIssue[] => {
    const issues: SelectionValidationIssue[] = [];
    const variant = findVariantGroup(groups);
    const variantOk =
        !!variant && variant.options.some((o) => o.id === selection.variantOptionId);
    if (!variantOk) {
        issues.push({ key: 'error.option.variantRequired' });
    }
    for (const g of findAddOnGroups(groups)) {
        const count = g.options.filter((o) =>
            selection.addOnOptionIds.includes(o.id),
        ).length;
        if (count < g.minSelect) {
            issues.push({
                key: 'appointments.options.chooseAtLeast',
                n: g.minSelect,
                group: g.name,
            });
        } else if (count > g.maxSelect) {
            issues.push({
                key: 'appointments.options.chooseAtMost',
                n: g.maxSelect,
                group: g.name,
            });
        }
    }
    return issues;
};

/**
 * The additive wire fields for the owner create/update appointment payload.
 * Legacy (no bookable groups) ⇒ `{}` so both keys are OMITTED from the JSON
 * body — byte-identical legacy requests. With groups ⇒ the chosen variant id
 * plus only add-on ids that exist on the service (stale ids from a service
 * switch are dropped defensively; the server re-validates anyway).
 */
export const buildOptionSelectionPayload = (
    groups: ServiceOptionGroupDto[] | null | undefined,
    selection: AppointmentOptionSelection,
): { variantOptionId?: number; addOnOptionIds?: number[] } => {
    if (!hasBookableOptionGroups(groups)) return {};
    const validAddOnIds = new Set(
        findAddOnGroups(groups).flatMap((g) => g.options.map((o) => o.id)),
    );
    return {
        variantOptionId: selection.variantOptionId,
        addOnOptionIds: selection.addOnOptionIds.filter((id) => validAddOnIds.has(id)),
    };
};

/**
 * Edit-mode pre-selection from the appointment detail's itemized
 * `selectedOptions[]` (owner GET /salon-owner/appointments/{id}).
 *
 * Only ids that still exist on the service's current groups are honored —
 * legacy scalar-only appointments get a synthesized fallback line whose
 * `serviceOptionId` points at a legacy ServicePricingOption row, not a
 * ServiceOption, so it will (correctly) not match and the picker starts
 * from `defaultSelection`.
 */
export const deriveSelectionFromDetail = (
    groups: ServiceOptionGroupDto[],
    detailLines: AppointmentSelectedOptionLine[] | null | undefined,
): AppointmentOptionSelection => {
    if (!detailLines || detailLines.length === 0) return defaultSelection(groups);

    const variant = findVariantGroup(groups);
    const variantIds = new Set(variant?.options.map((o) => o.id) ?? []);
    const addOnIds = new Set(
        findAddOnGroups(groups).flatMap((g) => g.options.map((o) => o.id)),
    );

    const variantLine = detailLines.find(
        (l) => l.kind === 'Variant' && variantIds.has(l.serviceOptionId),
    );
    const addOnLines = detailLines.filter(
        (l) => l.kind === 'AddOn' && addOnIds.has(l.serviceOptionId),
    );

    if (!variantLine && addOnLines.length === 0) return defaultSelection(groups);
    return {
        variantOptionId: variantLine?.serviceOptionId ?? defaultSelection(groups).variantOptionId,
        addOnOptionIds: addOnLines.map((l) => l.serviceOptionId),
    };
};

/**
 * Children-first prefill for the LEGACY pricing-option dropdown in edit mode
 * (M57 pre-flight, AB#1970). The appointment detail's `selectedOptions[]` is
 * the durable source of truth: its Variant line carries the legacy option's
 * snapshotted NAME + price (M56 backfill / dual-write convention), so we
 * resolve the dropdown id from it by matching against the service's flat
 * legacy pricing options.
 *
 * Conservative by design — we NEVER guess:
 *   - no lines / no single Variant line     → `scalarFallback`
 *   - zero or ambiguous name matches        → price tie-break; still
 *     ambiguous (or no price match)         → `scalarFallback`
 *
 * `scalarFallback` is the wire `servicePricingOptionId`; that fallback (and
 * the scalar read feeding it) is deleted with the M57 column drop.
 */
export const deriveLegacyPrefillPricingOptionId = (
    detailLines: AppointmentSelectedOptionLine[] | null | undefined,
    legacyPricingOptions: Array<{ id: number; name: string; price: number }>,
    scalarFallback: number,
): number => {
    const variantLines = (detailLines ?? []).filter((l) => l.kind === 'Variant');
    if (variantLines.length !== 1) return scalarFallback;
    const [line] = variantLines;

    const byName = legacyPricingOptions.filter((p) => p.name === line.name);
    if (byName.length === 1) return byName[0].id;
    if (byName.length > 1) {
        const byPrice = byName.filter((p) => p.price === line.price);
        if (byPrice.length === 1) return byPrice[0].id;
    }
    return scalarFallback;
};

/**
 * Legacy dual-write scalar for the request's `pricingOptionId` while groups
 * are in play. The backend resolves the authoritative legacy row from
 * `variantOptionId` itself when the flag is ON (resolver `MapVariantToLegacy`),
 * so this value is only load-bearing on the flag-OFF path; we best-effort
 * match the chosen variant's name against the flat legacy list (the M54
 * backfill / dual-write keeps names aligned), falling back to the first row.
 */
export const matchLegacyPricingOptionId = (
    groups: ServiceOptionGroupDto[],
    selection: AppointmentOptionSelection,
    legacyPricingOptions: Array<{ id: number; name: string }>,
): number => {
    const variant = findVariantGroup(groups);
    const chosen = variant?.options.find((o) => o.id === selection.variantOptionId);
    if (chosen) {
        const byName = legacyPricingOptions.find((p) => p.name === chosen.name);
        if (byName) return byName.id;
    }
    return legacyPricingOptions[0]?.id ?? 0;
};
